/**
 * La Sura - category rail controller (sections/lasura-category-rail.liquid).
 *
 * The row is a native overflow-x scroller, so touch and touchpad swipes, Shift+wheel and
 * keyboard focus already work without this file. This adds what a mouse user is missing:
 *   - drag to scroll, without the drag turning into a click on the tile under the pointer;
 *   - vertical wheel scrolls the row sideways, but only for a wheel gesture that STARTS over
 *     the row while the page is at rest, and only until the row reaches an end. A page scroll
 *     that happens to pass over the row keeps scrolling the page, so the row never traps
 *     someone reading down the page;
 *   - previous/next arrows, the scroll position bar, and edge fades (via data attributes);
 *   - a one-off staggered entrance when the row first comes into view.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* One wheel gesture = a run of wheel events less than GESTURE_GAP ms apart. We note which rail
   (if any) the pointer was over when the run began; only that rail may take the run. */
const GESTURE_GAP = 220;
let lastWheelAt = -Infinity;
let gestureOwner = null;

document.addEventListener(
  'wheel',
  (event) => {
    if (event.timeStamp - lastWheelAt > GESTURE_GAP) {
      gestureOwner = event.target instanceof Element ? event.target.closest('[data-rail-track]') : null;
    }
    lastWheelAt = event.timeStamp;
  },
  { capture: true, passive: true }
);

/* Someone scrolling down the page a notch at a time sends wheel events far enough apart to look
   like new gestures. If the page itself moved in the last PAGE_QUIET ms, they are reading down
   the page, so the wheel stays with the page even when the row slides under the pointer. */
const PAGE_QUIET = 500;
let lastPageScrollAt = -Infinity;
window.addEventListener('scroll', (event) => (lastPageScrollAt = event.timeStamp), { passive: true });

class LasuraCategoryRail extends HTMLElement {
  #abort = null;
  #resize = null;
  #reveal = null;
  #frame = 0;
  #suppressClick = false;

  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.thumb = this.querySelector('[data-rail-thumb]');

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.track.addEventListener('scroll', this.#queueUpdate, { passive: true, signal });
    this.track.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.track.addEventListener('click', this.#onClickCapture, { capture: true, signal });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    // Without this a mouse drag also selects text across the page, as Horizon's slideshow found.
    this.track.addEventListener(
      'mousedown',
      (event) => {
        if (event.button === 0 && this.#max > 1) event.preventDefault();
      },
      { signal }
    );
    if (this.hasAttribute('data-wheel')) {
      this.track.addEventListener('wheel', this.#onWheel, { passive: false, signal });
    }
    this.prev?.addEventListener('click', () => this.#page(-1), { signal });
    this.next?.addEventListener('click', () => this.#page(1), { signal });

    this.#resize = new ResizeObserver(this.#queueUpdate);
    this.#resize.observe(this.track);
    this.#update();

    this.#setupReveal();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    this.#reveal?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #max() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  #queueUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const { track } = this;
    const max = this.#max;
    const scrollable = max > 1;
    const left = Math.min(Math.max(track.scrollLeft, 0), Math.max(max, 0));
    const atStart = left <= 1;
    const atEnd = left >= max - 1;

    this.toggleAttribute('data-scrollable', scrollable);
    this.toggleAttribute('data-at-start', atStart);
    this.toggleAttribute('data-at-end', atEnd);

    if (this.prev) this.prev.disabled = !scrollable || atStart;
    if (this.next) this.next.disabled = !scrollable || atEnd;

    if (this.thumb && scrollable) {
      const visible = track.clientWidth / track.scrollWidth;
      const progress = left / max;
      this.style.setProperty('--lasura-rail-visible', visible.toFixed(4));
      this.style.setProperty('--lasura-rail-shift', `${((progress * (1 - visible)) / visible) * 100}%`);
    }
  }

  /* Arrows move by most of a screen so the last tile of this view becomes the first of the next. */
  #page(direction) {
    this.track.scrollBy({
      left: direction * this.track.clientWidth * 0.8,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  #onWheel = (event) => {
    if (event.ctrlKey || event.shiftKey) return; // pinch-zoom, and the browser's own Shift+wheel
    if (gestureOwner !== this.track) return; // the gesture began elsewhere on the page
    if (event.timeStamp - lastPageScrollAt < PAGE_QUIET) return; // mid-way down the page
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return; // sideways swipe: native

    const max = this.#max;
    if (max <= 1) return;

    let delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 32; // lines (Firefox)
    else if (event.deltaMode === 2) delta *= this.track.clientWidth; // pages

    const left = this.track.scrollLeft;
    if ((delta < 0 && left <= 0) || (delta > 0 && left >= max - 1)) return; // at an end: page scrolls

    event.preventDefault();
    this.track.scrollLeft = left + delta;
  };

  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.#max <= 1) return;

    const { track } = this;
    const startX = event.clientX;
    const startLeft = track.scrollLeft;
    let lastX = startX;
    let lastTime = event.timeStamp;
    let velocity = 0;
    let dragging = false;

    const move = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < 6) return;
        dragging = true;
        this.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
      const dt = moveEvent.timeStamp - lastTime || 16;
      velocity = (moveEvent.clientX - lastX) / dt;
      lastX = moveEvent.clientX;
      lastTime = moveEvent.timeStamp;
    };

    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (!dragging) return;

      this.#suppressClick = true;
      setTimeout(() => (this.#suppressClick = false), 0); // no click follows if released off the row
      this.classList.remove('is-dragging');
      // Let the row glide on a little after a flick, then settle on a tile.
      if (!reducedMotion.matches && Math.abs(velocity) > 0.2) {
        track.scrollBy({ left: -velocity * 220, behavior: 'smooth' });
      }
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  /* A drag ends with a click on whatever tile is under the pointer; swallow that one click. */
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;
    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #setupReveal() {
    if (reducedMotion.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;

    // Already on screen when the script runs (e.g. a short hero): no entrance, just show it.
    const rect = this.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) return;

    this.classList.add('is-armed');
    this.#reveal = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.classList.add('is-in');
        this.#reveal?.disconnect();
        this.#reveal = null;
      },
      { rootMargin: '0px 0px -12% 0px' }
    );
    this.#reveal.observe(this);
  }
}

if (!customElements.get('lasura-category-rail')) {
  customElements.define('lasura-category-rail', LasuraCategoryRail);
}
